import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BASELINE } from '../../util';
import Message from './message';
import { motion } from "framer-motion";
import { FaAngleDoubleDown, FaComments } from "react-icons/fa";
import { IoIosSend } from "react-icons/io";

const AIAudioPlayer = ({ result, context, currentQuestion, type1, type2, isChatVisible, setIsChatVisible }) => {
  const voiceOptions = {
    gender: 'Female',
    voiceName: 'en-US-AriaNeural',
    rate: '15%',
    pitch: '9%',
    volume: '80%',
  };

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleSteps, setVisibleSteps] = useState(1);
  const [userInput, setUserInput] = useState('');
  const [error, setError] = useState(null);
  const [isHidden, setIsHidden] = useState(false);
  const [isPlayingVoice, setIsPlayingVoice] = useState(true);
  const [chatLoaded, setChatLoaded] = useState(false);

  // To track only user-AI conversation, separate from step-by-step
  const [conversationHistory, setConversationHistory] = useState([]);

  // 스크롤 대상 ref 생성
  const messagesEndRef = useRef(null);
  // 현재 캐시 저장에 사용할 question ID를 ref로 추적 (state 타이밍 문제 방지)
  const activeQuestionIdRef = useRef(null);

  // Initialize chat history storage if not exists
  useEffect(() => {
    if (!window.chatHistory) {
      window.chatHistory = {};
    }
  }, []);

  // Handle visibility changes
  useEffect(() => {
    if (isHidden) {
      setIsPlayingVoice(false);
    } else {
      setIsPlayingVoice(true);
    }
  }, [isHidden]);

  // Handle isChatVisible changes
  useEffect(() => {
    if (isChatVisible === true) {
      setIsHidden(false);
    } else {
      setIsHidden(true);
    }
  }, [isChatVisible]);

  // Load chat history or fetch new AI response when question or context changes
  useEffect(() => {
    if (!currentQuestion?.questionIds || !context?.trim()) return;

    // 새 문제로 전환 시 상태 초기화 (이전 문제 내용이 잠깐 보이는 현상 방지)
    setMessages([]);
    setConversationHistory([]);
    setVisibleSteps(1);
    setChatLoaded(false);
    activeQuestionIdRef.current = currentQuestion.questionIds;

    if (window.chatHistory?.[currentQuestion.questionIds]) {
      const savedChat = window.chatHistory[currentQuestion.questionIds];
      setMessages(savedChat.messages);
      setVisibleSteps(savedChat.visibleSteps);
      setConversationHistory(savedChat.conversationHistory || []);
      setIsLoading(false);
      setChatLoaded(true);
    } else {
      fetchAIResponse();
    }
  }, [currentQuestion, context]);

  // Save chat whenever messages or visibleSteps change
  // activeQuestionIdRef 사용으로 currentQuestion state 타이밍 문제 방지
  useEffect(() => {
    if (chatLoaded && activeQuestionIdRef.current && messages.length > 0) {
      if (!window.chatHistory) window.chatHistory = {};
      window.chatHistory[activeQuestionIdRef.current] = {
        messages,
        visibleSteps,
        conversationHistory
      };
    }
  }, [messages, visibleSteps, conversationHistory, chatLoaded]);

  // Auto-scroll when messages or visibleSteps change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, visibleSteps]);

  // Hide or Expand functionality
  const toggleChat = () => {
    setIsHidden(!isHidden);
  };

  // Fetch AI response for initial steps
  const genprompt = async (contextText) => {
    try {
      let data;
      if (currentQuestion.pPccs === "P89#CAS") {
        data = {
          prompt: contextText,
          qid: currentQuestion.questionIds,
        };
      }
      else if (currentQuestion.programs === "SAT") {
        data = {
          prompt: contextText,
          qid: currentQuestion.questionIds,
        };
      }
      else {
        data = {
          prompt: contextText,
          qid: currentQuestion.question_id,
        };
      }

      if (type1 && type1.trim() !== '') {
        data.type = type1;
      } else if (type2 && type2.trim() !== '') {
        data.type = type2;
      }

      const res = await axios.post(`${BASELINE}askai`, data);
      if (res.data && res.data.message) {
        const parsedData = JSON.parse(res.data.message);
        return parsedData;
      } else {
        console.error('Unexpected response structure:', res.data);
        return {
          greeting: ['Error: Unexpected response from the server.'],
          stepbystep: [],
        };
      }
    } catch (error) {
      console.error('Error during API call:', error);
      return { greeting: ['Loading...'], stepbystep: [] };
    }
  };

  const fetchAIResponse = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const aiData = await genprompt(context);
      const allMessages = [];
      if (aiData.greeting && aiData.greeting.length > 0) {
        allMessages.push({ type: 'greeting', text: aiData.greeting[0], indexs: 0 });
      }
      if (aiData.stepbystep && aiData.stepbystep.length > 0) {
        aiData.stepbystep.forEach((step, idx) => {
          allMessages.push({ type: 'step', text: step, indexs: idx + 1 });
        });
      }
      setMessages(allMessages);
      // Initialize empty conversation history
      setConversationHistory([]);
      // Initially only show the first message
      setVisibleSteps(1);
      setChatLoaded(true);
    } catch (error) {
      console.error('Error fetching AI response:', error);
      setError('Failed to get AI response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const showNextStep = () => {
    if (visibleSteps < messages.length) {
      setVisibleSteps(visibleSteps + 1);
    }
  };

  const handleAsk = async () => {
    if (!userInput.trim()) {
      setError('Please enter a question before asking.');
      return;
    }

    const visibleMessages = messages.slice(0, visibleSteps);
    const remainingMessages = messages.slice(visibleSteps);
    
    // Only send the actual conversation history, not the step-by-step guides
    // Create updated conversation history with current question
    const updatedConversation = [
      ...conversationHistory,
      { role: "user", content: userInput }
    ];

    // 프롬프트에서 대화 이력을 제거함
    let question = `
  You are a friendly SAT tutor who helps students solve problems step-by-step without giving away the answer. Keep your responses concise (under 100 tokens) and avoid repeating phrases.
  
  **Guidelines:**
  - Skip greetings like "Hi" or "Hello."
  - Never reveal the answer directly; let the student discover it.
  - If the student provides the correct answer (${currentQuestion.correct_answer}), respond with enthusiastic praise only (e.g., "That's exactly right! Well done!").
  
  **When the student is stuck or answers incorrectly:**
  - Offer one clear hint.
  - Ask one targeted guiding question.
  
  **If the student thanks you after a correct answer:**
  - Reply briefly and kindly (e.g., "You're welcome!" or "Great job!").
  
  **Problem Details:**
  Question: ${currentQuestion.stem}
  Prompt: ${currentQuestion.prompt}
  Stimulus: ${currentQuestion.stimulus}
  Options:
  A) ${currentQuestion.a}
  B) ${currentQuestion.b}
  C) ${currentQuestion.c}
  D) ${currentQuestion.d}
  Correct Answer: ${currentQuestion.correct_answer}
  Rationale: ${currentQuestion.rationale}
  
  Previous conversation:
  ${updatedConversation.map(msg => `${msg.role === 'user' ? 'Student' : 'Tutor'}: ${msg.content}`).join('\n')}
  
  Student's current question: ${userInput}
  `;

    // Save the user's input temporarily
    const userQuestion = userInput;
    // Clear input field immediately for better UX
    setUserInput('');

    try {
      const res = await axios.post(`${BASELINE}askai`, {
        prompt: question,
        // Send only the conversation history, not step-by-step guides
        chatHistory: updatedConversation.map(msg => msg.content)
      });

      if (res.data && res.data.message) {
        const answer = res.data.message.trim();
        
        // Update the messages array with user question and AI answer
        const updatedMessages = [
          ...visibleMessages,
          { type: 'userQuestion', text: userQuestion, indexs: null },
          { type: 'aiAnswer', text: answer, indexs: null },
          ...remainingMessages,
        ];
        
        // Also update conversation history with both user question and AI response
        const newConversationHistory = [
          ...updatedConversation,
          { role: "assistant", content: answer }
        ];
        
        setMessages(updatedMessages);
        setConversationHistory(newConversationHistory);
        setVisibleSteps(visibleSteps + 2);
      } else {
        console.error('Unexpected response structure:', res.data);
        setError('Failed to get a valid response from the server.');
      }
    } catch (error) {
      console.error('Error during API call:', error);
      setError('Failed to get a response. Please try again.');
    }
  };

  return (
    <>
      {isHidden && (
        <motion.div
          className="hidden md:flex fixed bottom-[90px] right-[8px] sm:right-[20px] w-[60px] h-[60px] items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 text-white text-2xl shadow-md hover:shadow-lg cursor-pointer rounded-full z-[900] transition-all duration-300"
          onClick={toggleChat}
          whileHover={{ scale: 1.05, boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.1)" }}
          whileTap={{ scale: 0.95 }}
        >
          <FaComments />
        </motion.div>
      )}

      {!isHidden && (
        <div className="fixed bottom-[3px] right-0 sm:right-[15px] w-full sm:w-[550px] h-[80vh] sm:h-[700px] flex flex-col rounded-lg shadow-xl z-[900] overflow-hidden">
          {/* 배경 블러 효과 */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900 opacity-95 backdrop-blur-sm" />

          {/* 헤더 */}
          <div className="z-[100] flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-base font-bold text-white flex items-center">
                <span className="h-2 w-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                AI Assistant
              </span>
            </div>
            <button
              onClick={() => { toggleChat(); setIsChatVisible(false); }}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors duration-200 text-gray-300 hover:text-white"
            >
              <FaAngleDoubleDown />
            </button>
          </div>

          {/* 채팅 메시지 영역 */}
          <div className="z-[100] flex-grow overflow-y-auto p-4 space-y-4 no-scrollbar bg-opacity-30">
            {isLoading ? (
              <div className="m-2 p-4 rounded-md bg-blue-900 bg-opacity-30 border border-blue-700 text-sm flex items-start">
                <div className="mr-3">
                  <svg
                    className="w-5 h-5 text-blue-400 animate-spin"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 2a8 8 0 00-8 8v4a2 2 0 002 2h4v-4H6v-2a4 4 0 118 0v2h-2v4h4a2 2 0 002-2v-4a8 8 0 00-8-8z" />
                  </svg>
                </div>
                <p className="text-blue-200">AI thinking...</p>
              </div>
            ) : (
              <>
                {result === true && (
                  <Message
                    key={-1}
                    indexs={-1}
                    text={'Congratulations! You have successfully solved this problem!'}
                    voiceOptions={isPlayingVoice ? voiceOptions : null}
                  />
                )}
                {result === false && (
                  <Message
                    key={-2}
                    indexs={-2}
                    text={"Let's try this again later!"}
                    voiceOptions={isPlayingVoice ? voiceOptions : null}
                  />
                )}
                {messages.slice(0, visibleSteps).map((message, index) => (
                  <Message
                    key={index}
                    indexs={message.indexs}
                    text={message.text}
                    voiceOptions={isPlayingVoice ? voiceOptions : null}
                    onGetMoreHelp={showNextStep}
                    type={message.type}
                    showGetMoreHelp={
                      index !== message.index &&
                      (message.type === 'step' || message.type === 'greeting') &&
                      index !== messages.length - 1  // Add this condition to hide button on last message
                    }
                  />
                ))}
                {/* 스크롤용 빈 div */}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* 입력 영역 */}
          {!isLoading && (
            <div className="z-[100] flex items-center gap-3 p-3 border-t border-gray-700 bg-gray-800 bg-opacity-80 w-full">
              <input
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Type your question..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAsk();
                  }
                }}
                className="bg-gray-700 rounded-full px-4 py-2.5 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-gray-600 transition-all duration-200 flex-grow text-sm"
              />
              <button
                onClick={handleAsk}
                disabled={!userInput.trim()}
                className={`rounded-full transition-all duration-200 p-2.5 min-w-[50px] flex-shrink-0 flex items-center justify-center ${userInput.trim()
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md hover:shadow-lg hover:from-blue-600 hover:to-blue-700'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
              >
                <IoIosSend className={userInput.trim() ? 'text-white' : 'text-gray-400'} />
              </button>
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div className="px-4 py-2 bg-red-900 bg-opacity-80 text-red-200 border-t border-red-700 text-sm">
              <div className="flex items-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2 text-red-400" fill="currentColor">
                  <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" />
                </svg>
                {error}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIAudioPlayer;